// Tour guidé pour expliquer à l'utilisateur comment s'abonner aux notifications
// (carte / agenda). Met en surbrillance les zones clés, ajoute un voile sur
// les éléments à ignorer, et fait apparaître un panneau d'instructions par étape.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Button, IconButton, Stack, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export interface GuidedTourStep {
  // CSS selector OU une fonction qui retourne un HTMLElement (pour des refs dynamiques)
  target: string | (() => HTMLElement | null);
  title: string;
  body: string;
  // Position du panneau d'instructions par rapport à la cible.
  placement?: 'right' | 'bottom' | 'left' | 'top' | 'center';
  // Optionnel : action à exécuter au passage à cette étape (ex: ouvrir le drawer)
  onEnter?: () => void;
  // Liste de sélecteurs CSS à voiler pour cette étape (réduire les distractions)
  dimSelectors?: string[];
}

interface Props {
  open: boolean;
  steps: GuidedTourStep[];
  onClose: () => void;
}

const HIGHLIGHT_PADDING = 8;

const resolveTarget = (target: GuidedTourStep['target']): HTMLElement | null => {
  if (typeof target === 'function') return target();
  return document.querySelector(target) as HTMLElement | null;
};

const GuidedTourOverlay: React.FC<Props> = ({ open, steps, onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const step = steps[stepIndex];

  // Calcule la position du highlight uniquement à l'entrée + sur événements
  // (scroll, resize, mutation DOM). Pas de boucle d'animation continue.
  useEffect(() => {
    if (!open || !step) return undefined;

    let scheduled = false;
    const measure = () => {
      const el = resolveTarget(step.target);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect(r);
      if (r.top < 0 || r.bottom > window.innerHeight) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        measure();
      });
    };

    measure();

    // Petit délai pour laisser le DOM se mettre en place après onEnter.
    const t1 = setTimeout(measure, 80);
    const t2 = setTimeout(measure, 300);
    const t3 = setTimeout(measure, 700);

    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule, { passive: true });

    // Observe les changements du DOM (drawer qui s'ouvre, contenu chargé)
    const observer = typeof MutationObserver !== 'undefined' ? new MutationObserver(schedule) : null;
    observer?.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
    };
  }, [open, step]);

  // Effet de bord à l'entrée d'une étape
  useEffect(() => {
    if (!open || !step) return undefined;
    step.onEnter?.();
    return undefined;
  }, [open, step]);

  // Voile additionnel sur les sélecteurs à atténuer
  useEffect(() => {
    if (!open || !step) return undefined;
    const dimmed: HTMLElement[] = [];
    (step.dimSelectors || []).forEach((sel) => {
      document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
        el.dataset.ouaaaDimPrev = el.style.opacity || '';
        el.dataset.ouaaaPointer = el.style.pointerEvents || '';
        el.style.opacity = '0.25';
        el.style.pointerEvents = 'none';
        el.style.transition = 'opacity 0.4s';
        dimmed.push(el);
      });
    });
    return () => {
      dimmed.forEach((el) => {
        el.style.opacity = el.dataset.ouaaaDimPrev || '';
        el.style.pointerEvents = el.dataset.ouaaaPointer || '';
        delete el.dataset.ouaaaDimPrev;
        delete el.dataset.ouaaaPointer;
      });
    };
  }, [open, step]);

  const handleNext = useCallback(() => {
    if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1);
    else {
      try {
        localStorage.setItem('ouaaa_tour_completed', String(Date.now()));
      } catch {}
      onClose();
    }
  }, [stepIndex, steps.length, onClose]);

  const handlePrev = useCallback(() => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  }, [stepIndex]);

  const handleSkip = useCallback(() => {
    try {
      localStorage.setItem('ouaaa_tour_skipped', String(Date.now()));
    } catch {}
    onClose();
  }, [onClose]);

  // Calcule la position du panneau d'instructions
  const tooltipStyle = useMemo<React.CSSProperties>(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    if (!rect || step?.placement === 'center') {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }
    // Sur mobile, on force le panneau au-dessus de la cible pour qu'elle
    // reste visible en dessous (et on prend toute la largeur dispo).
    const placement = isMobile ? 'top' : (step?.placement || 'bottom');
    const gap = 16;
    const tooltipWidth = isMobile
      ? (typeof window !== 'undefined' ? Math.min(window.innerWidth - 16, 360) : 320)
      : 360;
    const tooltipMaxHeight = 220;

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'right':
        top = rect.top + rect.height / 2 - tooltipMaxHeight / 2;
        left = rect.right + gap;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipMaxHeight / 2;
        left = rect.left - tooltipWidth - gap;
        break;
      case 'top':
        top = rect.top - tooltipMaxHeight - gap;
        left = isMobile ? 8 : rect.left + rect.width / 2 - tooltipWidth / 2;
        // Si le panneau dépasse en haut, on bascule en bas
        if (top < 8) {
          top = rect.bottom + gap;
        }
        break;
      case 'bottom':
      default:
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
    }

    // Clamp dans le viewport
    if (typeof window !== 'undefined') {
      top = Math.max(8, Math.min(top, window.innerHeight - tooltipMaxHeight - 8));
      left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
    }

    return { top, left, width: tooltipWidth };
  }, [rect, step]);

  if (!open || !step) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        pointerEvents: 'none',
      }}
    >
      {/* Voile sombre avec trou autour du highlight */}
      {rect ? (
        <svg
          style={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'auto',
          }}
        >
          <defs>
            <mask id="ouaaa-tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - HIGHLIGHT_PADDING}
                y={rect.top - HIGHLIGHT_PADDING}
                width={rect.width + HIGHLIGHT_PADDING * 2}
                height={rect.height + HIGHLIGHT_PADDING * 2}
                rx="8"
                ry="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(15,20,40,0.55)"
            mask="url(#ouaaa-tour-mask)"
            onClick={handleSkip}
          />
          <rect
            x={rect.left - HIGHLIGHT_PADDING}
            y={rect.top - HIGHLIGHT_PADDING}
            width={rect.width + HIGHLIGHT_PADDING * 2}
            height={rect.height + HIGHLIGHT_PADDING * 2}
            rx="8"
            ry="8"
            fill="none"
            stroke="#fcc100"
            strokeWidth="3"
            style={{ filter: 'drop-shadow(0 0 8px #fcc10080)' }}
          />
        </svg>
      ) : (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(15,20,40,0.55)',
            pointerEvents: 'auto',
          }}
          onClick={handleSkip}
        />
      )}

      {/* Panneau d'instructions */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          ...tooltipStyle,
          maxWidth: 'calc(100vw - 32px)',
          p: 2.5,
          borderRadius: 2,
          pointerEvents: 'auto',
          border: '2px solid #fcc100',
          background: 'white',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#d96552', fontWeight: 700 }}>
            Étape {stepIndex + 1} / {steps.length}
          </Typography>
          <IconButton size="small" onClick={handleSkip} aria-label="Quitter le tour">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="h6" sx={{ color: '#2c367e', mb: 1 }}>
          {step.title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          {step.body}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button
            onClick={handlePrev}
            disabled={stepIndex === 0}
            startIcon={<ArrowBackIcon />}
            size="small"
          >
            Précédent
          </Button>
          <Button
            onClick={handleNext}
            variant="contained"
            color="secondary"
            endIcon={stepIndex < steps.length - 1 ? <ArrowForwardIcon /> : undefined}
            size="small"
          >
            {stepIndex < steps.length - 1 ? 'Suivant' : 'Terminer'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default GuidedTourOverlay;
